import { useContext, useEffect, useMemo, useRef, useState, useSyncExternalStore } from 'react'
import { AuthContext } from '@core/auth/useAuth'
import { MapPin, X } from 'lucide-react'
import * as THREE from 'three'
import { useAppStore } from '@core/store'
import { isFeatureEnabled } from '@core/features'
import { isWorkReadBlocked, subscribeRecovery } from '@core/session/recovery'
import { latestParsed } from '@modules/bcf/store/bcfClientSlice'
import { agruparTopicos } from '@modules/bcf/lib/bcfAgrupar'
import {
  viewpointsStore,
  type LocalViewpoint,
} from '@viewer/lib/viewpointsStore'
import { semCena } from '@viewer/lib/viewpointCena'
import { applyLocalViewpoint } from '@viewer/lib/viewpointCapture'
import { viewerApi } from '@viewer/lib/viewerApi'
import { pinVisible, statusKey, type PinTopic } from './lib/pinFilter'
import { ancoraDoPin } from './lib/pinAncoraCena'
import { ESPERA_DE_ESTABILIDADE_MS, podeCalcularAncoras, type Ponto } from './lib/pinAncora'
import {
  acaoDaBolha,
  agruparEmBolhas,
  chaveDoConjunto,
  CLASSES_DO_TOM,
  diametroDaBolha,
  ROTULO_DO_TOM,
  tomDaMarca,
  type Bolha,
} from './lib/pinBolhas'
import { corteDoEstado, dentroDoCorte, haCorte } from './lib/pinCorte'
import { calcularOclusao, ESPERA_DA_CAMARA_MS, profundidade, type Camara } from './lib/pinOclusao'
import { lancadorDaCena } from './lib/pinOclusaoCena'
import { usePinFiltro } from './usePinFiltro'
import { enderecoDoTopico, irPara } from './lib/pinTopico'

interface Candidato extends PinTopic {
  topicGuid: string
  bcfLocalId: string
  viewpoint: LocalViewpoint
}

interface Pin extends Candidato {
  anchor: Ponto
}

interface ScreenPin extends Pin {
  /** Identidade do pin no ecrã (tópico e viewpoint). */
  chave: string
  x: number
  y: number
  /** Profundidade ao longo do olhar, para a ordem de desenho. */
  depth: number
  onScreen: boolean
  /** Dentro do volume do corte (sempre, sem corte). */
  dentro: boolean
}

/** Uma marca a desenhar: um pin sozinho ou uma bolha, numa camada. */
interface Marca {
  bolha: Bolha<ScreenPin>
  esbatida: boolean
  /** A profundidade do pin mais próximo da bolha. */
  depth: number
}

/** Quantas âncoras se calculam antes de redesenhar os pins. */
const LOTE_DE_ANCORAS = 10

/** Espera antes de testar a oclusão depois de mudarem os pins, o filtro
 *  ou o corte: as âncoras chegam em lotes, e cada lote mudaria os pins;
 *  sem esta espera, cada lote lançava uma passagem que o seguinte
 *  cancelava. */
const ESPERA_DA_OCLUSAO_MS = 100

/** Opacidade dos pins tapados ou fora do corte. */
const OPACIDADE_ESBATIDA = 0.35

/** Chave de uma âncora calculada no estado do overlay: o tópico, o
 *  viewpoint e os modelos abertos (outros modelos, outra posição). */
function chaveDoPin(c: Pick<Candidato, 'topicGuid' | 'viewpoint'>, modelosKey: string): string {
  return `${c.topicGuid}|${c.viewpoint.id}|${modelosKey}`
}

/** A câmara de agora, para a profundidade. */
function camaraDoMundo(camera: THREE.Camera): Camara {
  const dir = camera.getWorldDirection(new THREE.Vector3())
  const pos = camera.getWorldPosition(new THREE.Vector3())
  return { posicao: [pos.x, pos.y, pos.z], direcao: [dir.x, dir.y, dir.z] }
}

/** Pins sobre o modelo para os viewpoints associados a tópicos BCF.
 *
 *  Um pin por tópico com viewpoint. Uma detecção de conflitos triada em
 *  BCF põe um pin por conflito; para continuarem legíveis:
 *
 *  - os que ficam a menos de `RAIO_DA_BOLHA_PX` uns dos outros no ecrã
 *    juntam-se numa bolha com o número (pinBolhas.ts); clicar aproxima a
 *    câmara, ou abre a lista quando aproximar já não separa;
 *  - os tapados pela geometria esbatem-se (ou escondem-se), testados por
 *    raio quando a câmara pára (pinOclusao.ts);
 *  - com corte activo, os de fora do volume escondem-se (ou esbatem-se)
 *    (pinCorte.ts);
 *  - desenham-se do mais longe para o mais perto, os esbatidos por baixo.
 *
 *  O painel `BcfPinsPanel` filtra-os; o estado lido aqui é o dele.
 *
 *  A posição é a `anchor` do viewpoint (capturados na plataforma e
 *  tópicos dos conflitos) ou, sem ela (BCF importados), a calculada a
 *  pedido em pinAncoraCena.ts: só para os pins que passam o filtro, com
 *  os pins visíveis, e em cache por tópico. */
export default function BcfPinsOverlay() {
  const loaded = useAppStore((state) => state.bcfLoaded)
  const visible = useAppStore((state) => state.bcfPinsVisible)
  const models = useAppStore((state) => state.models)
  const setPinTopics = useAppStore((state) => state.setBcfPinTopics)
  const setResumo = useAppStore((state) => state.setBcfPinsResumoVista)
  const esconderTapados = useAppStore((state) => state.bcfPinsEsconderTapados)
  const esconderForaDoCorte = useAppStore((state) => state.bcfPinsEsconderForaDoCorte)
  const sectionEnabled = useAppStore((state) => state.sectionEnabled)
  const sectionOrientation = useAppStore((state) => state.sectionOrientation)
  const sectionExtents = useAppStore((state) => state.sectionExtents)
  const planosDeCorte = useAppStore((state) => state.planosDeCorte)
  const filtro = usePinFiltro()
  // Clicar num pin aplica o viewpoint e abre o tópico no módulo BCF (o
  // mesmo `?topic=` que os links partilhados usam). Sem o módulo (modo
  // local, ou workspace sem ele), fica só o viewpoint.
  // O contexto lê-se sem exigir o provider: o overlay também se monta
  // sem sessão (testes, e o viewer antes de a sessão estar pronta).
  const auth = useContext(AuthContext)
  const abrirPin = (pin: { viewpoint: Parameters<typeof applyLocalViewpoint>[0]; bcfLocalId?: string; topicGuid?: string }) => {
    void applyLocalViewpoint(pin.viewpoint)
    const destino = enderecoDoTopico({
      modoCloud: auth?.mode === 'cloud',
      workspace: auth?.workspace?.slug ?? null,
      moduloBcf: auth?.modules.includes('bcf') ?? false,
      bcfLocalId: pin.bcfLocalId ?? null,
      topicGuid: pin.topicGuid ?? null,
    })
    if (destino) irPara(destino)
  }
  const [candidatos, setCandidatos] = useState<Candidato[]>([])
  // Âncoras calculadas (null: sem posição possível), por chaveDoPin.
  const [calculadas, setCalculadas] = useState<ReadonlyMap<string, Ponto | null>>(() => new Map())
  const [worldVersion, setWorldVersion] = useState(0)
  // Conta as paragens da câmara (ESPERA_DA_CAMARA_MS sem mexer).
  const [paragens, setParagens] = useState(0)
  // Pins tapados, pela chave do ecrã. Fica o resultado da última
  // paragem enquanto a câmara se mexe: recalcular a cada fotograma
  // seria um raio por pin por fotograma.
  const [tapados, setTapados] = useState<ReadonlySet<string>>(() => new Set())
  // A lista de uma bolha que aproximar já não separa. Vale para a câmara
  // em que foi aberta (`versao`): mexer na câmara fecha-a.
  const [lista, setLista] = useState<{ x: number; y: number; pins: ScreenPin[]; versao: number } | null>(null)
  // A última bolha para onde a câmara foi aproximada: um segundo clique
  // na mesma bolha abre a lista.
  const ultimaAproximada = useRef<string | null>(null)
  const enabled = isFeatureEnabled('bcfPins')
  const worldReady = useSyncExternalStore(
    viewerApi.subscribe,
    () => viewerApi.getWorld() !== null,
    () => false,
  )
  const modelosKey = useMemo(() => models.map((m) => `${m.id}:${m.sha256}`).join('|'), [models])
  const corte = useMemo(
    () => corteDoEstado({ sectionEnabled, sectionOrientation, sectionExtents, planosDeCorte }),
    [sectionEnabled, sectionOrientation, sectionExtents, planosDeCorte],
  )
  // Modelos a abrir (uma abertura ou um lote, como a recuperação da
  // sessão) e a pergunta da recuperação por responder: não se calculam
  // âncoras (ver podeCalcularAncoras).
  const aCarregar = useSyncExternalStore(
    viewerApi.subscribe,
    () => viewerApi.aCarregarModelos(),
    () => false,
  )
  const recuperacaoDecidida = useSyncExternalStore(
    subscribeRecovery,
    () => !isWorkReadBlocked(),
    () => true,
  )
  // O conjunto de modelos que já está parado há ESPERA_DE_ESTABILIDADE_MS.
  const [modelosParados, setModelosParados] = useState<string | null>(null)
  useEffect(() => {
    if (aCarregar) return
    const t = setTimeout(() => setModelosParados(modelosKey), ESPERA_DE_ESTABILIDADE_MS)
    return () => clearTimeout(t)
  }, [modelosKey, aCarregar])
  const podeCalcular = podeCalcularAncoras({
    ligado: enabled,
    visivel: visible,
    mundoPronto: worldReady,
    recuperacaoDecidida,
    aCarregar,
    modelosEstaveis: modelosParados === modelosKey,
  })

  const topicRefs = useMemo(() => {
    const refs: Array<PinTopic & { topicGuid: string; bcfLocalId: string }> = []
    for (const bcf of loaded) {
      const parsed = latestParsed(bcf)
      // As extensões do próprio ficheiro dizem que estados são
      // terminais. Não se adivinha pelo texto: cada projecto usa as
      // suas palavras.
      const terminais = new Set(
        parsed.extensions.statuses
          .filter((s) => s.isTerminal)
          .map((s) => s.label),
      )
      // O grupo de cada tópico, com o nome dos blocos do módulo BCF
      // (etiqueta "Grupo <id>" ou RelatedTopics): o filtro Grupo do
      // painel mostra os mesmos nomes que a lista do módulo.
      const grupoDe = new Map<string, { chave: string; nome: string }>()
      for (const b of agruparTopicos(parsed.topics, 'grupo', { extensions: parsed.extensions })) {
        if (b.semValor) continue
        const grupo = { chave: `${bcf.localId}|${b.chave}`, nome: b.nome }
        for (const t of b.topicos) grupoDe.set(t.guid, grupo)
      }
      for (const topic of parsed.topics) {
        for (const id of topic.viewpointRefs) {
          refs.push({
            id,
            title: topic.title,
            status: topic.status,
            closed: topic.status ? terminais.has(topic.status) : false,
            source: bcf.name,
            topicGuid: topic.guid,
            bcfLocalId: bcf.localId,
            priority: topic.priority,
            assignedTo: topic.assignedTo,
            labels: topic.labels,
            description: topic.description,
            grupo: grupoDe.get(topic.guid) ?? null,
          })
        }
      }
    }
    return refs
  }, [loaded])

  useEffect(() => {
    if (!enabled) return
    let cancelled = false
    void Promise.all(
      topicRefs.map(async (ref) => {
        const viewpoint = await viewpointsStore.get(ref.id)
        // Uma imagem anexada não tem posição.
        if (!viewpoint || semCena(viewpoint.viewpoint)) return null
        return { ...ref, viewpoint }
      }),
    ).then((rows) => {
      if (!cancelled) setCandidatos(rows.filter((row): row is Candidato => row !== null))
    })
    return () => {
      cancelled = true
    }
  }, [enabled, topicRefs])

  // As âncoras que faltam, a pedido: só com os pins visíveis, os modelos
  // estáveis (nenhum a abrir) e a recuperação decidida, só para os que
  // passam o filtro, e em lotes. As que já estão na cache entram no
  // primeiro lote sem custo. Se uma abertura começar a meio, pára; volta
  // a correr quando os modelos estabilizarem.
  useEffect(() => {
    if (!podeCalcular) return
    const porCalcular = candidatos.filter((c) => !c.viewpoint.anchor && pinVisible(c, filtro))
    if (porCalcular.length === 0) return
    let cancelled = false
    void (async () => {
      for (let i = 0; i < porCalcular.length; i += LOTE_DE_ANCORAS) {
        const lote = new Map<string, Ponto | null>()
        for (const c of porCalcular.slice(i, i + LOTE_DE_ANCORAS)) {
          if (cancelled || viewerApi.aCarregarModelos()) return
          const p = await ancoraDoPin(c.topicGuid, c.viewpoint).catch(() => null)
          lote.set(chaveDoPin(c, modelosKey), p)
        }
        if (cancelled) return
        setCalculadas((antes) => {
          const proximo = new Map(antes)
          for (const [k, v] of lote) proximo.set(k, v)
          return proximo
        })
      }
    })()
    return () => {
      cancelled = true
    }
  }, [podeCalcular, candidatos, filtro, modelosKey])

  const pins = useMemo<Pin[]>(() => {
    const out: Pin[] = []
    for (const c of candidatos) {
      const anchor = c.viewpoint.anchor ?? calculadas.get(chaveDoPin(c, modelosKey)) ?? null
      if (anchor) out.push({ ...c, anchor })
    }
    return out
  }, [candidatos, calculadas, modelosKey])

  // O painel precisa de saber que tópicos existem para os poder listar
  // e contar. Publicar daqui evita que ele repita a leitura do
  // IndexedDB só para desenhar as caixas de visto. Entram os que têm
  // posição e os que ainda a podem ter (por calcular); saem os que já se
  // sabe que não têm.
  useEffect(() => {
    setPinTopics(
      candidatos
        .filter((c) => {
          if (c.viewpoint.anchor) return true
          const k = chaveDoPin(c, modelosKey)
          return !calculadas.has(k) || calculadas.get(k) !== null
        })
        .map(({ viewpoint: _viewpoint, ...topico }) => topico),
    )
  }, [candidatos, calculadas, modelosKey, setPinTopics])

  // A câmara: cada fotograma reprojecta; uma paragem (sem mexer durante
  // ESPERA_DA_CAMARA_MS) pede o teste da oclusão.
  useEffect(() => {
    const controls = viewerApi.getWorld()?.camera.controls
    if (!controls) return
    let parada: ReturnType<typeof setTimeout> | null = null
    const update = () => {
      setWorldVersion((value) => value + 1)
      if (parada) clearTimeout(parada)
      parada = setTimeout(() => setParagens((n) => n + 1), ESPERA_DA_CAMARA_MS)
    }
    controls.addEventListener('update', update)
    window.addEventListener('resize', update)
    return () => {
      if (parada) clearTimeout(parada)
      controls.removeEventListener('update', update)
      window.removeEventListener('resize', update)
    }
  }, [worldReady])

  const screenPins = useMemo<ScreenPin[]>(() => {
    void worldVersion
    if (!enabled || !visible) return []
    const world = viewerApi.getWorld()
    if (!world) return []
    const canvas = world.renderer?.three.domElement
    if (!canvas) return []
    const rect = canvas.getBoundingClientRect()
    const camara = camaraDoMundo(world.camera.three)
    return pins
      .filter((pin) => pinVisible(pin, filtro))
      .map((pin) => {
        const projected = new THREE.Vector3(...pin.anchor).project(
          world.camera.three,
        )
        return {
          ...pin,
          chave: `${pin.topicGuid}:${pin.id}`,
          x: ((projected.x + 1) / 2) * rect.width,
          y: ((1 - projected.y) / 2) * rect.height,
          depth: profundidade(pin.anchor, camara),
          onScreen:
            projected.z >= -1 &&
            projected.z <= 1 &&
            projected.x >= -1.1 &&
            projected.x <= 1.1 &&
            projected.y >= -1.1 &&
            projected.y <= 1.1,
          dentro: dentroDoCorte(pin.anchor, corte),
        }
      })
  }, [enabled, visible, pins, worldVersion, filtro, corte])

  // A oclusão, quando a câmara pára (e quando mudam os pins, o filtro, o
  // corte ou os modelos). Só os pins no ecrã e dentro do corte; os de
  // fora do corte já estão escondidos ou esbatidos. Uma nova paragem
  // cancela a passagem em curso.
  const screenPinsRef = useRef<ScreenPin[]>([])
  useEffect(() => {
    screenPinsRef.current = screenPins
  }, [screenPins])
  const pinsParaOclusao = useMemo(() => pins.filter((p) => pinVisible(p, filtro)), [pins, filtro])
  useEffect(() => {
    if (!enabled || !visible || pinsParaOclusao.length === 0) return
    let cancelado = false
    const t = setTimeout(() => {
      void (async () => {
        try {
          const world = viewerApi.getWorld()
          const lancar = lancadorDaCena()
          if (!world || !lancar || viewerApi.aCarregarModelos()) return
          const pedidos = screenPinsRef.current
            .filter((p) => p.onScreen && p.dentro)
            .map((p) => ({ chave: p.chave, ancora: p.anchor, x: p.x, y: p.y }))
          const r = await calcularOclusao(pedidos, lancar, {
            camara: camaraDoMundo(world.camera.three),
            corte,
            cancelado: () => cancelado,
          })
          if (!cancelado && r.completo) setTapados(r.tapados)
        } catch (err) {
          // Um raycast que rebenta não pode deitar abaixo os pins: ficam
          // como estavam (na dúvida, visíveis).
          console.debug('[pins] oclusão ignorada', err)
        }
      })()
    }, ESPERA_DA_OCLUSAO_MS)
    return () => {
      cancelado = true
      clearTimeout(t)
    }
  }, [enabled, visible, paragens, pinsParaOclusao, corte, modelosKey])

  // As marcas: as camadas (normal e esbatida), cada uma agrupada em
  // bolhas, e a ordem de desenho (esbatidas por baixo; dentro de cada
  // camada, do mais longe para o mais perto).
  const { marcas, foraDoCorte, nTapados } = useMemo(() => {
    const normais: ScreenPin[] = []
    const esbatidos: ScreenPin[] = []
    let fora = 0
    let tap = 0
    for (const p of screenPins) {
      if (!p.dentro) {
        fora += 1
        if (esconderForaDoCorte) continue
        if (p.onScreen) esbatidos.push(p)
        continue
      }
      const tapado = tapados.has(p.chave)
      if (tapado) {
        tap += 1
        if (esconderTapados) continue
      }
      if (!p.onScreen) continue
      ;(tapado ? esbatidos : normais).push(p)
    }
    const camada = (ps: ScreenPin[], esbatida: boolean): Marca[] =>
      agruparEmBolhas(ps)
        .map((bolha) => ({ bolha, esbatida, depth: Math.min(...bolha.pins.map((p) => p.depth)) }))
        .sort((a, b) => b.depth - a.depth)
    return {
      marcas: [...camada(esbatidos, true), ...camada(normais, false)],
      foraDoCorte: fora,
      nTapados: tap,
    }
  }, [screenPins, tapados, esconderTapados, esconderForaDoCorte])

  const nBolhas = marcas.filter((m) => m.bolha.pins.length > 1).length
  useEffect(() => {
    setResumo({ tapados: nTapados, foraDoCorte: haCorte(corte) ? foraDoCorte : 0, bolhas: nBolhas })
  }, [nTapados, foraDoCorte, nBolhas, corte, setResumo])

  const clicarBolha = (b: Bolha<ScreenPin>) => {
    const chaves = b.pins.map((p) => p.chave)
    const acao = acaoDaBolha({
      ancoras: b.pins.map((p) => p.anchor),
      chaves,
      ultimaAproximada: ultimaAproximada.current,
    })
    if (acao.tipo === 'lista') {
      setLista({ x: b.x, y: b.y, pins: b.pins, versao: worldVersion })
      return
    }
    const world = viewerApi.getWorld()
    if (!world) return
    ultimaAproximada.current = chaveDoConjunto(chaves)
    const caixa = new THREE.Box3(new THREE.Vector3(...acao.caixa.min), new THREE.Vector3(...acao.caixa.max))
    void world.camera.controls
      .fitToBox(caixa, true, { paddingTop: 0.2, paddingBottom: 0.2, paddingLeft: 0.2, paddingRight: 0.2 })
      .catch((err: unknown) => console.warn('[pins] aproximar à bolha falhou', err))
  }

  const listaAberta = lista && lista.versao === worldVersion ? lista : null

  return (
    <div className="pointer-events-none absolute inset-0 z-20 overflow-hidden">
      {marcas.map(({ bolha, esbatida }) => {
        const estilo = { left: bolha.x, top: bolha.y, opacity: esbatida ? OPACIDADE_ESBATIDA : 1 }
        const tom = tomDaMarca(bolha.pins)
        if (bolha.pins.length === 1) {
          const pin = bolha.pins[0]
          return (
            <button
              key={pin.chave}
              type="button"
              data-pin={pin.chave}
              data-esbatido={esbatida ? 'sim' : undefined}
              className={`pointer-events-auto absolute -translate-x-1/2 -translate-y-full rounded-full border p-1 shadow-lg hover:scale-110 ${CLASSES_DO_TOM[tom]}`}
              style={estilo}
              title={`${pin.title}${
                pin.status ? ` · ${statusKey(pin)}` : ''
              }${pin.priority ? ` · ${pin.priority}` : ''} · ${pin.source}${
                esbatida ? (pin.dentro ? '\nTapado pela geometria.' : '\nFora do corte.') : ''
              }\nClica para aplicar o viewpoint e abrir o tópico.`}
              onClick={() => abrirPin(pin)}
            >
              <MapPin size={16} aria-hidden="true" />
              <span className="sr-only">{pin.title}</span>
            </button>
          )
        }
        const d = diametroDaBolha(bolha.pins.length)
        return (
          <button
            key={`bolha:${bolha.chave}`}
            type="button"
            data-bolha={bolha.pins.length}
            data-esbatido={esbatida ? 'sim' : undefined}
            className={`pointer-events-auto absolute flex -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full border-2 text-[11px] font-semibold shadow-lg hover:scale-110 ${CLASSES_DO_TOM[tom]}`}
            style={{ ...estilo, width: d, height: d }}
            title={`${bolha.pins.length} tópicos juntos neste ponto do ecrã · cor: ${ROTULO_DO_TOM[tom]}${
              esbatida ? ' · tapados ou fora do corte' : ''
            }\nClica para aproximar; se já não der para separar, abre a lista.`}
            onClick={() => clicarBolha(bolha)}
          >
            {bolha.pins.length}
          </button>
        )
      })}
      {listaAberta && (
        <div
          role="dialog"
          aria-label="Tópicos neste ponto"
          className="pointer-events-auto absolute z-10 w-64 rounded-md border border-[var(--color-border)] bg-[var(--color-panel)] text-xs shadow-2xl"
          style={{ left: Math.max(4, listaAberta.x + 14), top: Math.max(4, listaAberta.y - 14) }}
          onKeyDown={(e) => {
            if (e.key === 'Escape') setLista(null)
          }}
        >
          <header className="flex items-center gap-2 border-b border-[var(--color-border)] px-2 py-1.5">
            <strong>{listaAberta.pins.length} tópicos neste ponto</strong>
            <button type="button" className="ml-auto p-0.5" title="Fechar" onClick={() => setLista(null)}>
              <X size={12} />
            </button>
          </header>
          <ul className="max-h-60 overflow-y-auto py-1">
            {listaAberta.pins.map((pin) => (
              <li key={pin.chave}>
                <button
                  type="button"
                  className="flex w-full items-center gap-2 px-2 py-1 text-left hover:bg-[var(--color-surface)]"
                  onClick={() => {
                    setLista(null)
                    abrirPin(pin)
                  }}
                >
                  <span className={`h-2 w-2 shrink-0 rounded-full border ${CLASSES_DO_TOM[tomDaMarca([pin])]}`} />
                  <span className="min-w-0 flex-1 truncate">{pin.title}</span>
                  <span className="shrink-0 text-[10px] text-[var(--color-text-muted)]">{statusKey(pin)}</span>
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  )
}
